import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { LayoutDashboard, Loader2, LogOut, RefreshCw, Search, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

interface Customer { id: string; name: string; email: string }

export default function DashboardAccessUsers() {
  const { token, logout, openUserDashboard } = useAuth();
  const [users, setUsers] = useState<Customer[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState<string | null>(null);
  const [error, setError] = useState('');
  const selected = new URLSearchParams(window.location.search).get('user');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v1/dashboard-access/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(data.users || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Unable to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { void loadUsers(); }, [loadUsers]);

  const open = async (id: string) => {
    setOpening(id);
    setError('');
    try {
      await openUserDashboard(id);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Unable to open dashboard.');
      setOpening(null);
    }
  };
  const visible = users.filter((user) => `${user.name} ${user.email}`.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="flex items-center justify-between border-b bg-white px-6 py-5">
        <span className="text-xl font-bold">TechLadder.ai <span className="ml-2 text-sm font-normal text-slate-500">Dashboard Access</span></span>
        <button onClick={logout} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"><LogOut size={16} /> Sign Out</button>
      </header>
      <main className="mx-auto max-w-6xl p-6 sm:p-10">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div><h1 className="flex items-center gap-3 text-3xl font-bold"><Users className="text-violet-600" /> User Dashboards</h1><p className="mt-2 text-slate-500">Select a user to open their dashboard.</p></div>
          <button onClick={loadUsers} disabled={loading || !!opening} className="flex items-center gap-2 rounded-lg border bg-white px-4 py-2 disabled:opacity-50"><RefreshCw size={16} /> Refresh Users</button>
        </div>
        <div className="relative mb-6"><Search className="absolute left-3 top-3 text-slate-400" size={20} /><input aria-label="Search users" placeholder="Search by name or email..." value={query} onChange={(event) => setQuery(event.target.value)} className="w-full rounded-xl border bg-white py-3 pl-10 pr-4" /></div>
        {error && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>}
        {loading ? <div role="status" className="flex items-center justify-center gap-2 p-12"><Loader2 className="animate-spin" /> Loading users...</div> : (
          <>
            <p className="mb-4 text-sm text-slate-500">{visible.length} users</p>
            <div className="grid gap-4 md:grid-cols-2">
              {visible.map((user) => <article key={user.id} className={`grid min-h-28 grid-cols-1 items-center gap-4 rounded-xl border bg-white p-5 sm:grid-cols-[minmax(0,1fr)_auto] ${selected === user.id ? 'border-violet-400' : 'border-slate-200'}`}>
                <div className="min-w-0"><h2 className="truncate font-semibold" title={user.name || 'Unnamed User'}>{user.name || 'Unnamed User'}</h2><p className="truncate text-sm text-slate-500" title={user.email || user.id}>{user.email || user.id}</p></div>
                <button onClick={() => open(user.id)} disabled={!!opening} className="flex min-h-11 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-violet-700 px-4 py-2 text-sm font-medium text-white hover:bg-violet-800 disabled:opacity-50">
                  {opening === user.id ? <Loader2 size={16} className="animate-spin" /> : <LayoutDashboard size={16} />}{opening === user.id ? 'Opening...' : 'Open Dashboard'}
                </button>
              </article>)}
            </div>
            {!visible.length && <p className="py-12 text-center text-slate-500">No users found.</p>}
          </>
        )}
      </main>
    </div>
  );
}
